import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { reportsAPI, downloadBlob, soCreateAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { DownloadSimple, Package, Storefront } from '@phosphor-icons/react';
import SearchableSelect from '@/components/SearchableSelect';

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function PurchaseReportPage() {
  const [groupBy, setGroupBy] = useState('vendor');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [branchId, setBranchId] = useState('');
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    soCreateAPI.branches().then(({ data }) => setBranches(data.branches || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { group_by: groupBy };
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      if (branchId) params.branch_id = branchId;
      const { data } = await reportsAPI.purchase(params);
      setData(data);
    } catch (e) { toast.error('Failed to load purchase report'); }
    finally { setLoading(false); }
  }, [groupBy, start, end, branchId]);

  useEffect(() => { load(); }, [load]);

  const doExport = async (format) => {
    try {
      const params = { format, group_by: groupBy };
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      if (branchId) params.branch_id = branchId;
      const res = await reportsAPI.purchaseExport(params);
      downloadBlob(res, `purchase_report.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch { toast.error('Export failed'); }
  };

  const isItem = groupBy === 'item';

  return (
    <div data-testid="purchase-report-page">
      <PageHeader title="Purchase Report" subtitle="Vendor-wise and item-wise purchase analysis from vendor bills" />
      <div className="page-content space-y-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="inline-flex rounded-lg border border-[#E2E8F0] overflow-hidden" data-testid="purchase-groupby-toggle">
            <button onClick={() => setGroupBy('vendor')} className={`px-4 py-2 text-sm flex items-center gap-1.5 ${!isItem ? 'bg-[#0F4C81] text-white' : 'bg-white text-[#64748B]'}`} data-testid="groupby-vendor"><Storefront size={15} /> Vendor-wise</button>
            <button onClick={() => setGroupBy('item')} className={`px-4 py-2 text-sm flex items-center gap-1.5 ${isItem ? 'bg-[#0F4C81] text-white' : 'bg-white text-[#64748B]'}`} data-testid="groupby-item"><Package size={15} /> Item-wise</button>
          </div>
          <div><label className="text-xs text-[#64748B] block mb-1">From</label><Input type="date" value={start} onChange={e => setStart(e.target.value)} className="h-9 w-40" data-testid="purchase-from" /></div>
          <div><label className="text-xs text-[#64748B] block mb-1">To</label><Input type="date" value={end} onChange={e => setEnd(e.target.value)} className="h-9 w-40" data-testid="purchase-to" /></div>
          <div>
            <label className="text-xs text-[#64748B] block mb-1">Branch</label>
            <SearchableSelect value={branchId || 'all'} onChange={v => setBranchId(v === 'all' ? '' : v)} placeholder="All branches" testid="purchase-branch" buttonClassName="w-52 h-9"
                  options={[{ value: 'all', label: 'All branches' }, ...branches.map(b => ({ value: b.id, label: b.name, keywords: b.code || b.clinic_code || "" }))]} />
          </div>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={() => doExport('excel')} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="purchase-excel-btn"><DownloadSimple size={16} className="mr-1" />Excel</Button>
            <Button variant="outline" onClick={() => doExport('pdf')} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="purchase-pdf-btn"><DownloadSimple size={16} className="mr-1" />PDF</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-4"><p className="text-xs text-[#64748B] mb-1">{isItem ? 'Items' : 'Vendors'}</p><p className="text-lg font-semibold text-[#0F4C81]">{data.totals?.count || 0}</p></div>
          {isItem && <div className="bg-white border border-[#E2E8F0] rounded-lg p-4"><p className="text-xs text-[#64748B] mb-1">Total Qty</p><p className="text-lg font-semibold text-[#0F4C81]">{Number(data.totals?.quantity || 0).toLocaleString('en-IN')}</p></div>}
          <div className="bg-[#0F4C81] text-white rounded-lg p-4"><p className="text-xs text-white/70 mb-1">Total Purchase Value</p><p className="text-lg font-semibold">{fmt(data.totals?.total)}</p></div>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="purchase-table">
            <thead>
              <tr className="bg-[#F8FAFC] text-[#64748B] text-xs">
                {isItem ? (
                  <><th className="text-left p-3">Item</th><th className="text-left p-3">Code</th><th className="text-right p-3">Qty</th><th className="text-center p-3">Bills</th><th className="text-right p-3">Value</th></>
                ) : (
                  <><th className="text-left p-3">Vendor</th><th className="text-center p-3">Bills</th><th className="text-right p-3">Taxable</th><th className="text-right p-3">Tax</th><th className="text-right p-3">Grand Total</th></>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-10"><div className="inline-block w-5 h-5 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></td></tr>
              ) : data.rows.length > 0 ? data.rows.map((r, i) => (
                <tr key={i} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                  {isItem ? (
                    <>
                      <td className="p-3 font-medium text-[#0F172A]">{r.name || '(unnamed)'}</td>
                      <td className="p-3 text-[#64748B] font-mono text-xs">{r.product_code || '-'}</td>
                      <td className="p-3 text-right">{Number(r.quantity || 0).toLocaleString('en-IN')}</td>
                      <td className="p-3 text-center text-[#64748B]">{r.bill_count}</td>
                      <td className="p-3 text-right font-medium">{fmt(r.value)}</td>
                    </>
                  ) : (
                    <>
                      <td className="p-3 font-medium text-[#0F172A]">{r.vendor_name || '(unnamed)'}</td>
                      <td className="p-3 text-center text-[#64748B]">{r.bill_count}</td>
                      <td className="p-3 text-right">{fmt(r.taxable)}</td>
                      <td className="p-3 text-right">{fmt(r.tax)}</td>
                      <td className="p-3 text-right font-medium">{fmt(r.total)}</td>
                    </>
                  )}
                </tr>
              )) : (
                <tr><td colSpan={5} className="text-center py-10 text-[#64748B]">No purchase data for the selected filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
